import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import BackArrow from 'assets/back-arrow.svg';
import ThemeStore from 'stories/ThemeStore';
import { PaymentMethod } from '../shared/types';
import { Container, Section, Title } from '../shared/styled';
import { PaymentMethodCard } from './PaymentMethodCard';
import { StripeIcons, CryptoIcons } from './PaymentIcons';
import { BalanceDisplay } from '../components/BalanceDisplay';
import { TransactionsList } from '../components/TransactionsList';
import { ActionButton } from '../components/ActionButton';
import { ErrorTooltip } from '../components/ErrorTooltip';
import {
    Transaction,
    TransactionStatus,
    generateTransactionId
} from '../components/transactionTypes';
import {StatusFilter} from "components/Finance/components/StatusFilter";

interface TopUpBalanceProps {
    onBack: () => void;
}

export const TopUpBalance: React.FC<TopUpBalanceProps> = ({ onBack }) => {
    const [selectedPayment, setSelectedPayment] = useState<PaymentMethod | null>(null);
    const [amount, setAmount] = useState<string>('');
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [selectedStatus, setSelectedStatus] = useState<TransactionStatus | 'all'>('all');
    const theme = ThemeStore.activeTheme;

    useEffect(() => {
        const savedTransactions = localStorage.getItem('transactions');
        if (savedTransactions) {
            try {
                const parsed = JSON.parse(savedTransactions);
                const transactionsWithDates = parsed.map((t: any) => ({
                    ...t,
                    date: new Date(t.date)
                }));
                setTransactions(transactionsWithDates);
            } catch (error) {
                console.error('Failed to load transactions:', error);
            }
        }
    }, []);

    useEffect(() => {
        if (transactions.length > 0) {
            localStorage.setItem('transactions', JSON.stringify(transactions));
        }
    }, [transactions]);

    useEffect(() => {
        if (showError) {
            const timer = setTimeout(() => setShowError(false), 4000);
            return () => clearTimeout(timer);
        }
    }, [showError]);

    const isFormValid = !!(selectedPayment && amount && parseFloat(amount) > 0);

    const createTransaction = (
        paymentMethod: PaymentMethod,
        amount: number
    ): Transaction => {
        return {
            id: generateTransactionId(),
            date: new Date(),
            type: 'topup',
            paymentMethod,
            amount,
            status: 'completed' as TransactionStatus,
            details: `Top up via ${paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1)}`,
            cardLast4: paymentMethod === 'stripe' ? '1234' : undefined,
        };
    };

    const handleContinue = () => {
        if (!selectedPayment) {
            setErrorMessage('To continue, please select a payment method and enter the amount to top up your balance');
            setShowError(true);
            return;
        }

        if (!amount || parseFloat(amount) <= 0) {
            setErrorMessage('To continue, enter the amount to top up your balance');
            setShowError(true);
            return;
        }

        const newTransaction = createTransaction(selectedPayment, parseFloat(amount));
        setTransactions(prev => [newTransaction, ...prev]);
        setSelectedPayment(null);
        setAmount('');
        setShowError(false);

        console.log('Transaction created:', newTransaction);
    };

    const handlePaymentSelect = (method: PaymentMethod) => {
        setSelectedPayment(method);
        setAmount('');
        setShowError(false);
    };

    const handleAmountChange = (value: string) => {
        setAmount(value);
        setShowError(false);
    };

    const filteredTransactions = selectedStatus === 'all'
        ? transactions
        : transactions.filter(t => t.status === selectedStatus);

    return (
        <PaymentContainer>
            <LeftSection>
                <Section>
                    <BackButton onClick={onBack}>
                        <BackArrow color={theme.palette.text.breadcrumbs} />
                        <FormattedMessage
                            id="Finance.TopUp.Back"
                            defaultMessage="Financial Overview"
                        />
                    </BackButton>

                    <Title>
                        <FormattedMessage
                            id="Finance.TopUp.Title"
                            defaultMessage="Top Up Balance"
                        />
                    </Title>

                    <PaymentMethods>
                        <PaymentMethodCard
                            type="stripe"
                            label="Pay with Card / Wallets"
                            isSelected={selectedPayment === 'stripe'}
                            onSelect={() => handlePaymentSelect('stripe')}
                            icons={<StripeIcons />}
                            amount={amount}
                            onAmountChange={handleAmountChange}
                        />

                        <PaymentMethodCard
                            type="crypto"
                            label="Pay with Crypto"
                            isSelected={selectedPayment === 'crypto'}
                            onSelect={() => handlePaymentSelect('crypto')}
                            icons={<CryptoIcons />}
                            amount={amount}
                            onAmountChange={handleAmountChange}
                        />
                    </PaymentMethods>

                    <ButtonWrapper>
                        <ActionButton
                            onClick={handleContinue}
                            isActive={isFormValid}
                        >
                            <FormattedMessage
                                id="Finance.TopUp.Continue"
                                defaultMessage="Continue to payment"
                            />
                        </ActionButton>
                        {showError && <ErrorTooltip message={errorMessage} />}
                    </ButtonWrapper>
                </Section>
                <Section style={{ height: '100%' }}>
                    <TransactionsHeader>
                        <Title>
                            <FormattedMessage
                                id="Finance.TopUp.Transactions"
                                defaultMessage="Transactions"
                            />
                        </Title>
                        {transactions.length > 0 && (
                            <StatusFilter
                                selectedStatus={selectedStatus}
                                onStatusChange={setSelectedStatus}
                            />
                        )}
                    </TransactionsHeader>
                    <TransactionsList transactions={filteredTransactions} />
                </Section>
            </LeftSection>
            <Section style={{ height: 'auto' }}>
                <BalanceDisplay balance={0} />
            </Section>
        </PaymentContainer>
    );
};

const PaymentContainer = styled(Container)`
    flex-direction: row;
    gap: 22px;

    @media (max-width: 1280px) {
        flex-direction: column;
        gap: 12px;
    }
`;

const LeftSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 18px;
    flex: 1;
    min-height: calc(100vh - 146px);

    @media (max-width: 1024px) {
        min-height: auto;
        gap: 12px;
    }
`;

const BackButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    background: none;
    border: none;
    color: #00000080;
    font-size: 13px;
    font-weight: 400;
    cursor: pointer;
    padding: 0;
    transition: opacity 0.2s ease;
    font-family: SF Pro Display, sans-serif;

    &:hover {
        opacity: 0.7;
    }

    svg {
        width: 16px;
        height: 16px;
    }

    @media (max-width: 768px) {
        font-size: 12px;

        svg {
            width: 14px;
            height: 14px;
        }
    }
`;

const PaymentMethods = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;

    @media (max-width: 768px) {
        gap: 10px;
    }
`;

const ButtonWrapper = styled.div`
    position: relative;
    display: flex;
    justify-content: flex-end;
    margin-top: 15px;
    border-radius: 10px;

    button {
        width: 210px;
        height: 46px;
        border-radius: 10px;
        font-family: Outfit;
        font-weight: 400;
        font-size: 16px;
    }

    @media (max-width: 768px) {
        justify-content: center;
    }
`;

const TransactionsHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    @media (max-width: 768px) {
        margin-bottom: 12px;
    }
`;